import { AccessRecoverySignOut } from "./components/access/AccessRecoverySignOut";
import { AccessRecoverySwitchProject } from "./components/access/AccessRecoverySwitchProject";
import { RedesignProvider } from "./flags/RedesignProvider";
import { DesignV2Gate } from "./flags/DesignV2Gate";
import { Fragment, type ComponentType, type ReactNode } from "react";
import { StytchB2BProvider } from "@stytch/react/b2b";
import CssBaseline from "@mui/joy/CssBaseline";
import BootstrapShell from "./components/BootstrapShell";
import ProjectRefusalGate, {
    HostRefusedProjectNotice,
    RefusedProjectPage,
} from "./components/ProjectRefusalGate";

import { EmbedAuthProvider } from "./contexts/EmbedAuthContext";
import { PersonalScopeBinding } from "./contexts/PersonalScopeBinding";
import { TenantQueryClientProvider } from "./contexts/TenantQueryClientProvider";
import type { Platform } from "./platform/Platform";
import { PlatformProvider } from "./platform/PlatformProvider";
import { AppearanceProvider } from "./theme/AppearanceProvider";
import { MarkupOutboxReplayProvider } from "./components/fileviewer/markupmode/MarkupOutboxReplayProvider";
import { UnsavedMarkupPrompt } from "./components/fileviewer/markupmode/unsavedMarkupWork";

export interface AppProvidersProps {
    /** The host's platform, created and installed (`installPlatform`) before render. */
    platform: Platform;
    children: ReactNode;
    /** URL admission publishes the cache scope and must survive cache replacement. */
    admissionBoundary?: ComponentType<{ children: ReactNode }>;
}

/**
 * The window-level providers every host mounts once per window, in this order:
 * platform, Stytch, embed auth, optional URL admission, the refused-project gate, the
 * tenant React Query client, appearance. BootstrapShell themes pending/error screens independently so a design
 * switch cannot remount admission or discard the live query client; the project's Design v2
 * flag reaches `RedesignProvider` from inside the query client through `DesignV2Gate`, which holds
 * the pages until it is known, so none mounts in a design the project does not use.
 * Web admission owns its BrowserRouter above the cache that admission
 * replaces; desktop keeps a MemoryRouter per tab (`RouterAdapter`).
 * Permission admission must not block obtaining the selected project credential
 * or remount the URL admission owner when the protected cache rotates.
 * Stytch, embed auth and the query client must mount once
 * per window, never once per hosted tab -- a per-tab copy would run redundant
 * `onStateChange` subscriptions and fragment the query cache across tabs.
 * `assumeHydrated={false}` is the web's setting: the first render reports an
 * uninitialized member, then the effect hydrates it from the client.
 * `UnsavedMarkupPrompt` sits above them all, so a viewer's markup still on its way keeps
 * the tab-close prompt up after the viewer, or the tenant subtree, has gone.
 */
export function AppProviders({
    platform,
    children,
    admissionBoundary: AdmissionBoundary = Fragment,
}: AppProvidersProps) {
    return (
        <PlatformProvider platform={platform}>
            <UnsavedMarkupPrompt />
            <StytchB2BProvider stytch={platform.auth.stytchClient} assumeHydrated={false}>
                <EmbedAuthProvider>
                    <RedesignProvider>
                        <AdmissionBoundary>
                            <ProjectRefusalGate
                                refused={
                                    platform.projectAdmission.owner === "app" ? (
                                        <RefusedProjectPage />
                                    ) : (
                                        <HostRefusedProjectNotice />
                                    )
                                }
                            >
                                <TenantQueryClientProvider
                                    fallback={<BootstrapShell />}
                                    admissionRecovery={
                                        platform.projectAdmission.owner === "app" ? (
                                            <>
                                                <AccessRecoverySwitchProject />
                                                <AccessRecoverySignOut />
                                            </>
                                        ) : undefined
                                    }
                                >
                                    <MarkupOutboxReplayProvider />
                                    <DesignV2Gate fallback={<BootstrapShell />}>
                                        <PersonalScopeBinding>
                                            <AppearanceProvider>
                                                <CssBaseline />
                                                {children}
                                            </AppearanceProvider>
                                        </PersonalScopeBinding>
                                    </DesignV2Gate>
                                </TenantQueryClientProvider>
                            </ProjectRefusalGate>
                        </AdmissionBoundary>
                    </RedesignProvider>
                </EmbedAuthProvider>
            </StytchB2BProvider>
        </PlatformProvider>
    );
}
